import { useRef, useState, type KeyboardEvent, type SelectHTMLAttributes } from 'react';
import { Icon } from '../icons/Icon';

interface Option {
  value: string;
  label: string;
}

interface SelectMenuProps {
  id: string;
  options: readonly Option[];
  /** The attributes of the native select kept under the menu (value, onChange, disabled…). */
  select: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'>;
}

/** Sets the value of the native select as the browser would, so its `onChange` runs. */
function choose(select: HTMLSelectElement | null, value: string): void {
  if (select === null || select.value === value) return;
  Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set?.call(select, value);
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

/** The drop-down of the DS (rule 25): a button that opens a list with the choice marked, full
 *  keyboard (arrows, Home, End, Enter, Esc), and the native select kept under it, hidden, for the
 *  form and its `onChange`. */
export function SelectMenu({ id, options, select }: SelectMenuProps) {
  const native = useRef<HTMLSelectElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const value = String(select.value ?? select.defaultValue ?? '');
  const current = options.find((o) => o.value === value) ?? options[0];
  const openAt = () => {
    setActive(
      Math.max(
        0,
        options.findIndex((o) => o.value === value),
      ),
    );
    setOpen(true);
  };
  const pick = (index: number) => {
    const option = options[index];
    if (option !== undefined) choose(native.current, option.value);
    setOpen(false);
    document.getElementById(id)?.focus();
  };
  const onKey = (event: KeyboardEvent) => {
    const keys: Record<string, () => void> = {
      ArrowDown: () => (open ? setActive(Math.min(options.length - 1, active + 1)) : openAt()),
      ArrowUp: () => (open ? setActive(Math.max(0, active - 1)) : openAt()),
      Home: () => setActive(0),
      End: () => setActive(options.length - 1),
      Enter: () => (open ? pick(active) : openAt()),
      ' ': () => (open ? pick(active) : openAt()),
      Escape: () => setOpen(false),
    };
    const action = keys[event.key];
    if (action === undefined || (event.key === 'Escape' && !open)) return;
    event.preventDefault();
    // Esc closes the list only, not the tool or the sheet around it.
    event.stopPropagation();
    action();
  };
  return (
    <span className="cs">
      <select
        {...select}
        id={`${id}-native`}
        ref={native}
        className="cs-native"
        tabIndex={-1}
        aria-hidden="true"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        id={id}
        className="cs-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-activedescendant={open ? `${id}-opt-${String(active)}` : undefined}
        aria-invalid={select['aria-invalid']}
        aria-describedby={select['aria-describedby']}
        disabled={select.disabled}
        onClick={() => (open ? setOpen(false) : openAt())}
        onKeyDown={onKey}
        onBlur={() => setOpen(false)}
      >
        <span>{current?.label ?? ''}</span>
        <Icon name="chevron" size={14} />
      </button>
      {open ? (
        <ul className="cs-pop" role="listbox" id={`${id}-list`} aria-labelledby={id}>
          {options.map((o, index) => (
            <li
              key={o.value}
              id={`${id}-opt-${String(index)}`}
              role="option"
              aria-selected={o.value === value}
              className={index === active ? 'active' : undefined}
              // Chosen on mouse down, before the button loses the focus and closes the list.
              onMouseDown={(event) => {
                event.preventDefault();
                pick(index);
              }}
              onMouseEnter={() => setActive(index)}
            >
              <span>{o.label}</span>
              {o.value === value ? <Icon name="check" size={14} /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}
